import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import { LocalStorage } from 'src/app/shared/providers/local-storage/local-storage';
import { NetworkProvider } from 'src/app/shared/providers/network/network';
import { CrtData, ProfondeurHistorique } from '../../models/data';
import { CrtFavoriteType } from '../../models/synthethic-view';
import { CrtHttpErrorHandlerService } from '../../providers/http-error-handler.service';
import { CrtFavoriteService } from './favorite.service';
import { CrtHistoriquesService } from './historiques.service';
import { SiteService } from './site.service';
import { servicesUrl } from '../../config';
import {
  Observable,
  Subject,
  catchError,
  concatMap,
  exhaustMap,
  firstValueFrom,
  forkJoin,
  from,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { EmplacementSite } from '../../models/emplacementSite';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { EtatFonctionnel, EtatFonctionnelFilter } from '../shared-filter/shared-filter';
dayjs.extend(utc);
/**
 * Etend CrtDataWidgetService pour implementer la liste de données et l'ajout/suppression de données favorites
 */
@Injectable({
  providedIn: 'root',
})
export class CrtDataService extends LocalStorage {
  /**
   * Clef de stockage d'une donnée pour un site
   * @type {string}
   */
  public static readonly DATA_STORAGE_KEY = 'crt-${idSite}-datas-${dataId}';
  /**
   * clef de stockage d'une donnée historique pour la synthése d'un site
   * @type {string}
   */
  public static readonly HISTORIC_SYNTHESE_STORAGE_KEY = 'crt-${idSite}-historics-synthese-${dataId}';
  /**
   * Subject pour la mise à jours de la liste de donnée
   */
  public updateSubject$: Subject<CrtData[]>;
  /**
   * clef de stockage des données pour un site
   * @type {string}
   */
  private readonly DATAS_STORAGE_KEY = 'crt-${idSite}-datas';
  /**
   * clef de stockage d'une donnée historique pour un site
   * @type {string}
   */
  private readonly HISTORIC_STORAGE_KEY = 'crt-${idSite}-historics-${dataId}';
  /**
   * valeur max priorite pour affichage simple
   */
  private readonly limiteAffichageSimple = 8999;

  private deleteFavorisQueue = Promise.resolve();

  constructor(
    protected override storage: Storage,
    protected override network: NetworkProvider,
    protected http: HttpClient,
    private crtHttpErrorHandler: CrtHttpErrorHandlerService,
    private favoriteService: CrtFavoriteService,
    private crtHistoriqueService: CrtHistoriquesService,
    private siteService: SiteService
  ) {
    super();
    this.updateSubject$ = new Subject();
    this.serverRequestPeriod = 10;
  }

  public findDatasByEmplacementSite(idSite: number, forceRefresh = false, siteRefresh = true): Observable<CrtData[]> {
    const url = servicesUrl.getDonneesBySite({ idSite });
    const key = this.formatStorageKey(this.DATAS_STORAGE_KEY, { idSite });

    return this.shouldUseWebservice(key, forceRefresh).pipe(
      exhaustMap((shouldUseWebservice) => {
        if (shouldUseWebservice) {
          const fetchData$ = siteRefresh
            ? forkJoin(
                [this.http.get<CrtData[]>(url), this.siteService.getSiteByIdSite(idSite, true)],
                (data: CrtData[], site: EmplacementSite) => {
                  return [...data];
                }
              )
            : this.http.get<CrtData[]>(url);
          return fetchData$.pipe(
            tap(async (datas) => {
              await this.storage.set(key, datas);
              await this.setLastRequestDateToNow(key);
            }),
            catchError(async (e) => {
              return from(this.crtHttpErrorHandler.handleError(e)).pipe(
                switchMap(() => fromPromise(this.storage.get(key)))
              );
            })
          );
        } else {
          return fromPromise(this.storage.get(key));
        }
      }),
      tap((datas: CrtData[]) => {
        this.updateSubject$.next(datas);
      })
    );
  }

  public async invalidateDataListRequestDate(idSite: number): Promise<void> {
    const key = this.formatStorageKey(this.DATAS_STORAGE_KEY, { idSite });
    await this.invalidateStorageDate(key);
  }

  /**
   * Setter les statuts en envoie en cours pour les données en param pour le site
   * @param siteId L'identifiant du site
   * @param datas Toutes les données du site
   * @param dataIds Les id des données à passer en statut "envoie en cours"
   */
  public async setTransmissionInProgress(idSite: number, datas: CrtData[], dataIds: number[]) {
    const key = this.formatStorageKey(this.DATAS_STORAGE_KEY, { idSite });

    datas.forEach((data) => {
      if (dataIds.some((id) => data.id === id)) {
        data.envoieEnCours = true;
        data.discordant = false;
        data.etatEnvoi = true;
      }
    });

    this.updateSubject$.next(datas);
    await this.storage.set(key, datas);
  }

  public addFavoris(data: CrtData, idSite: number) {
    const key = this.formatStorageKey(this.DATAS_STORAGE_KEY, { idSite });

    const newFav = {
      type: data.estHistorique ? CrtFavoriteType.HISTO : CrtFavoriteType.DONNEE,
      idDonnee: data.id,
      ordre: 1,
    };

    return this.favoriteService.addFavorite(newFav, idSite).pipe(
      tap(async () => {
        let dataKey: string;
        if (data.estHistorique) {
          dataKey = this.formatStorageKey(CrtDataService.HISTORIC_SYNTHESE_STORAGE_KEY, {
            idSite: idSite,
            dataId: data.id,
          });
        } else {
          dataKey = this.formatStorageKey(CrtDataService.DATA_STORAGE_KEY, {
            idSite: idSite,
            dataId: data.id,
          });
        }

        // maj dans la liste des données
        const datas = (await this.storage.get(key)) || [];
        await this.storage.set(key, [
          ...datas.map((d) => {
            if (d.id === data.id) {
              return {
                ...d,
                estFavoris: true,
              };
            }
            return d;
          }),
        ]);

        // on stock la donnée pour la récuperer dans la vue de synthese
        await this.storage.set(dataKey, data);
      })
    );
  }

  public deleteFavoris(data: CrtData, idSite: number) {
    this.deleteFavorisQueue = this.deleteFavorisQueue.then(async () => {
      const key = this.formatStorageKey(this.DATAS_STORAGE_KEY, { idSite });
      const favoriteType = data.estHistorique ? CrtFavoriteType.HISTO : CrtFavoriteType.DONNEE;

      try {
        const favorite = await firstValueFrom(
          this.favoriteService.getFavoriteFromStorage(idSite, favoriteType, data.id)
        );

        await firstValueFrom(this.favoriteService.removeFavorite(favorite, idSite));

        let dataKey: string;
        if (data.estHistorique) {
          dataKey = this.formatStorageKey(CrtDataService.HISTORIC_SYNTHESE_STORAGE_KEY, {
            idSite: idSite,
            dataId: data.id,
          });
        } else {
          dataKey = this.formatStorageKey(CrtDataService.DATA_STORAGE_KEY, {
            idSite: idSite,
            dataId: data.id,
          });
        }

        const datas = (await this.storage.get(key)) || [];
        await this.storage.set(key, [
          ...datas.map((d) => {
            if (d.id === data.id) {
              return {
                ...d,
                estFavoris: false,
              };
            }
            return d;
          }),
        ]);
        await this.storage.remove(dataKey);
      } catch (error) {
        console.error('Erreur lors de la suppression du favori:', error);
      }
    });

    return this.deleteFavorisQueue;
  }

  public getHistoriquesDonneesByIds(
    ids: number[],
    idSite: number,
    forceRefresh = false,
    serverRequestPeriod = 60
  ): Observable<CrtData[]> {
    const keyById = {};
    ids.forEach(
      (id) =>
        (keyById[id] = this.formatStorageKey(this.HISTORIC_STORAGE_KEY, {
          idSite: idSite,
          dataId: id,
        }))
    );

    return forkJoin(
      ids.map((id) => {
        return this.crtHistoriqueService.getProfondeurHistoIdSite(idSite).pipe(
          concatMap((profondeur) => {
            if (profondeur === ProfondeurHistorique.MOIS) {
              this.crtHistoriqueService.removeProfondeurHistoIdSite(idSite);
            }
            return this.shouldUseWebservice(
              keyById[id],
              forceRefresh || profondeur === ProfondeurHistorique.MOIS,
              serverRequestPeriod
            ).pipe(
              map((result) => {
                return { id: id, shouldUseWebservice: result };
              })
            );
          })
        );
      })
    ).pipe(
      exhaustMap((shouldUseWebservices) => {
        const obs: Observable<CrtData[]>[] = [];
        const loadFromWebService = shouldUseWebservices.filter((r) => r.shouldUseWebservice);
        const loadFromLocal = shouldUseWebservices.filter((r) => !r.shouldUseWebservice);

        if (loadFromWebService.length) {
          obs.push(this.getHistoriquesByIdsFromWebService(loadFromWebService, keyById));
        }

        const histoLocalData = loadFromLocal.map((l) => this.storage.get(keyById[l.id]) as Promise<CrtData>);

        obs.push(from(Promise.all(histoLocalData)));

        // Get all datas
        return forkJoin(obs).pipe(
          map((results) => {
            const sortedResults = [];
            // merge and sort crtData by ids
            [].concat(...results).forEach((crtData: CrtData) => {
              const idx = ids.indexOf(crtData.id);
              sortedResults[idx] = {
                ...crtData,
                valeursHistorique: crtData?.valeursHistorique?.map((histo) => ({
                  ...histo,
                  horodatage: dayjs.utc(histo.horodatage).toDate(),
                })),
              };
            });
            return sortedResults;
          })
        );
      })
    );
  }

  public getHistoriquesByIdsFromWebService(
    loadFromWebService: { id: number; shouldUseWebservice: boolean }[],
    storageKeysById: { [id: string]: string }
  ): Observable<CrtData[]> {
    // create query params and url with ids
    const queryParams = loadFromWebService.map((r) => `${r.id}`).join(',');
    const url = servicesUrl.donneesHistorique({ profondeurMois: ProfondeurHistorique.MOIS, idDonnees: queryParams });
    return this.http.get<CrtData[]>(url).pipe(
      tap((datas: CrtData[]) => {
        datas.forEach((data) => {
          this.storage
            .set(storageKeysById[data.id], data)
            .then(() => this.setLastRequestDateToNow(storageKeysById[data.id]));
        });
      }),
      catchError(async (e) => {
        await this.crtHttpErrorHandler.handleError(e);
        return [];
      })
    );
  }

  public async removeAllLastDatasRequestDateForSiteId(siteId: number) {
    const keys = await this.storage.keys();
    const siteKeysRemoved = keys
      .filter((k) => RegExp(new RegExp(`crt-${siteId}-datas${this.LAST_REQUEST_DATE_SUFFIX}`)).exec(k))
      .map((k) => this.storage.remove(k));
    await Promise.all(siteKeysRemoved);
  }

  public filterCrtDatas(
    datas: CrtData[],
    searchTerm: string,
    filterTerms: { [prop: string]: string[] },
    isHistoric: boolean = false,
    discordantOnly: boolean = false,
    isAffichageComplet: boolean = false,
    etatBlocage?: boolean,
    etatFonctionnel?: EtatFonctionnelFilter
  ): CrtData[] {
    const upperSearchTerm = searchTerm?.toLocaleUpperCase();

    return datas.filter(
      (data) =>
        !(discordantOnly && !data.discordant) &&
        (isHistoric ? data.estHistorique : true) &&
        (!upperSearchTerm || this.checkTermsfilter(data, upperSearchTerm)) &&
        (!filterTerms || this.checkFilterTerms(data, filterTerms)) &&
        (isAffichageComplet || data.prioriAff <= this.limiteAffichageSimple) &&
        (etatBlocage == null || etatBlocage === data.blocage) &&
        (!etatFonctionnel ||
          (etatFonctionnel.fonctionnel && data.etatFonctionnel === EtatFonctionnel.FONCTIONNEL) ||
          (etatFonctionnel.partiellementFonctionnel &&
            data.etatFonctionnel === EtatFonctionnel.PARTIELLEMENT_FONCTIONNEL) ||
          (etatFonctionnel.nonFonctionnel && data.etatFonctionnel === EtatFonctionnel.NON_FONCTIONNEL))
    );
  }

  /**
   * Vérification du champs de recherche
   * @param data
   * @param upperSearchTerm
   */
  public checkTermsfilter(data: CrtData, upperSearchTerm: string): boolean {
    if (
      !data.descriptionHierarNiv1?.toUpperCase().includes(upperSearchTerm) &&
      !data.descriptionHierarNiv2?.toUpperCase().includes(upperSearchTerm) &&
      !data.descriptionHierarNiv3?.toUpperCase().includes(upperSearchTerm) &&
      !data.descriptionMetier.toUpperCase().includes(upperSearchTerm) &&
      !data.descriptionDonnee.toUpperCase().includes(upperSearchTerm)
    ) {
      return false;
    }
    return true;
  }

  public checkFilterTerms(data: CrtData, filterTerms: { [prop: string]: string[] }): boolean {
    const requiredFilter = filterTerms['type'];
    if (!requiredFilter) {
      return false;
    }

    const filterProperties = {
      libelleInstallation: 'libelleInstallation',
      descriptionHierarNiv1: 'descriptionHierarNiv1',
      descriptionHierarNiv2: 'descriptionHierarNiv2',
      descriptionHierarNiv3: 'descriptionHierarNiv3',
      descriptionMetier: 'descriptionMetier',
      type: 'type',
    };

    return Object.entries(filterProperties).every(([key, property]) => {
      if (data[key] != null) {
        return filterTerms[key]?.includes(data[property]);
      }
      return true;
    });
  }

  public getDataByIdsAndIdSite(idSite: number, dataIds: number[], forceRefresh = false): Observable<CrtData[]> {
    const key = this.formatStorageKey(this.DATAS_STORAGE_KEY, { idSite });

    return this.shouldUseWebservice(key, forceRefresh).pipe(
      exhaustMap((shouldUseWebservice) =>
        shouldUseWebservice
          ? this.getDataByIdsFromApiAndIdSite(idSite, dataIds)
          : fromPromise(this.storage.get(key)).pipe(
              switchMap((datas: CrtData[]) => {
                if (!datas) {
                  return this.getDataByIdsFromApiAndIdSite(idSite, dataIds);
                }
                const datasFounded = dataIds.map((id) => datas.find((data) => data.id === id)).filter(Boolean);
                const remainingIds = dataIds.filter((id) => !datasFounded.some((data) => data.id === id));

                return remainingIds.length
                  ? this.getDataByIdsFromApiAndIdSite(idSite, remainingIds).pipe(
                      map((newDatas) => datasFounded.concat(newDatas))
                    )
                  : of(datasFounded);
              })
            )
      )
    );
  }

  private getDataByIdsFromApiAndIdSite(idSite: number, datasId: number[]) {
    const url = servicesUrl.getDonneesById({ idDonnee: datasId.map((id) => `${id}`).join(',') });
    const key = this.formatStorageKey(this.DATAS_STORAGE_KEY, { idSite });
    return this.http.get<CrtData[]>(url).pipe(
      tap(async (crtDatas) => {
        const datas = (await this.storage.get(key)) || [];
        crtDatas.forEach((crtData) => {
          const idx = datas.findIndex(({ id }) => id === crtData.id);
          if (idx !== -1) {
            datas.splice(idx, 1);
          }
          datas.push(crtData);
        });
        await this.storage.set(key, datas);
      })
    );
  }
}
